import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => details.classList.add('menu-open'));

    trapFocus(details);
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach(reset);
      } else {
        trapFocus(this.refs.details);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}

function sftRefreshLegacyNavigation() {
  const currentLinks = [
    ['Growth Marketing', '/pages/growth-marketing'],
    ['AI Implementation', '/pages/ai-implementation'],
    ['Work', '/pages/work'],
    ['Pricing', '/pages/pricing'],
    ['About', '/pages/about'],
  ];

  document.querySelectorAll('nav.sft-nav, nav[header-menu].sft-nav').forEach((nav) => {
    const text = nav.textContent || '';
    if (!text.includes('Growth System') && !text.includes('Sprints') && !text.includes('Home')) return;
    nav.innerHTML = `<ul class="sft-nav__list" role="menubar">${currentLinks
      .map(
        ([label, href]) =>
          `<li class="sft-nav__item" role="presentation"><a href="${href}" class="sft-nav__link" role="menuitem">${label}</a></li>`
      )
      .join('')}</ul>`;
  });

  const drawerSelectors = [
    ['#HeaderDrawer-home', 'Growth Marketing', '/pages/growth-marketing'],
    ['#HeaderDrawer-growth-system', 'AI Implementation', '/pages/ai-implementation'],
    ['#HeaderDrawer-sprints', 'Work', '/pages/work'],
    ['#HeaderDrawer-about', 'Pricing', '/pages/pricing'],
    ['#HeaderDrawer-book-a-call', 'About', '/pages/about'],
  ];

  drawerSelectors.forEach(([selector, label, href]) => {
    const link = document.querySelector(selector);
    if (!link) return;
    link.href = href;
    const text = link.querySelector('.menu-drawer__menu-item-text') || link;
    text.textContent = label;
  });

  const existingAbout = document.querySelector('#HeaderDrawer-sft-about-added');
  if (!existingAbout) {
    const drawerList = document.querySelector('#HeaderDrawer-book-a-call')?.closest('ul');
    const contactItem = document.querySelector('#HeaderDrawer-book-a-call')?.closest('li');
    if (drawerList && contactItem) {
      const aboutItem = contactItem.cloneNode(true);
      const aboutLink = aboutItem.querySelector('a');
      if (aboutLink) {
        aboutLink.id = 'HeaderDrawer-sft-about-added';
        aboutLink.href = '/pages/about';
        const aboutText = aboutLink.querySelector('.menu-drawer__menu-item-text') || aboutLink;
        aboutText.textContent = 'About';
      }
      drawerList.insertBefore(aboutItem, contactItem.nextSibling);
    }
  }

  document.querySelectorAll('a.sft-header-cta, a[href="/pages/contact"]').forEach((cta) => {
    const text = (cta.textContent || '').trim();
    if (text === 'Book a Call') cta.textContent = 'Book a call';
  });
}

sftRefreshLegacyNavigation();
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', sftRefreshLegacyNavigation, { once: true });
} else {
  sftRefreshLegacyNavigation();
}
setTimeout(sftRefreshLegacyNavigation, 150);
